/**
 * This custom hook provides the functionality to edit a course form.
 */

import React, {useEffect, useState} from "react";
import useLoadSelectableUsersPlaylists from "./useLoadSelectableUsersPlaylists";
import {addDays} from "date-fns";

const useGeneralEditCourseForm = (selectedCourse, editedCourse) => {

    // Preparation of a simplified current date as basis to set a default from/to dateTime
    const currentDateWithoutSecondsMinutes = new Date()
    currentDateWithoutSecondsMinutes.setSeconds(0)
    currentDateWithoutSecondsMinutes.setMinutes(0)

    // Editable values (course values) - used to control the controlled elements' states
    const [participants, setParticipants] = useState(selectedCourse?.participants || [])
    const [playlists, setPlaylists] = useState(selectedCourse?.playlists || [])
    const [name, setName] = React.useState(selectedCourse?.name);
    const [fromDate, setFromDate] = React.useState(selectedCourse?.fromDate || currentDateWithoutSecondsMinutes);
    const [toDate, setToDate] = React.useState(selectedCourse?.toDate || addDays(currentDateWithoutSecondsMinutes, 7));
    const [association, setAssociation] = React.useState(selectedCourse?.association || '');
    const [disciplines, setDisciplines] = React.useState(selectedCourse?.disciplines || []);
    const [open, setOpen] = React.useState(selectedCourse?.open || false);

    const [selectablePlaylists, setSelectablePlaylists] = useState([])
    const [selectableUsers, setSelectableUsers] = useState([])

    const [loading, setLoading] = useState(false)

    // Selectable values in the multi select dropdowns
    const {fetchSelectableUsers, fetchSelectablePlaylists} = useLoadSelectableUsersPlaylists()


    // If the some state changes, also the reference needs to be updated
    useEffect(() => {
        editedCourse.current = {
            ...editedCourse.current,
            key: selectedCourse?.key,
            name: name,
            fromDate: fromDate,
            toDate: toDate,
            association: association,
            disciplines: disciplines,
            participants: participants,
            playlists: playlists,
            open: open
        }
    }, [name, fromDate, toDate, association, disciplines, participants, playlists, open])

    // Fetch the selectable users and playlists based on the association selection
    useEffect(() => {
        if (association) {
            setLoading(true)
            Promise.all([fetchSelectableUsers(association), fetchSelectablePlaylists(association)])
                .then((results) => {
                    setSelectableUsers(results[0])
                    setSelectablePlaylists(results[1])
                })
                .finally(() => {
                    setLoading(false)
                })
        }
    }, [association])

    const handleChangeName = (event) => {
        setName(event.target.value)
    }

    const handleChangeFromDate = (date) => {
        date.setSeconds(0) // Save without the precise seconds
        setFromDate(date)
    }

    const handleChangeToDate = (date) => {
        date.setSeconds(0) // Save without the precise seconds
        setToDate(date)
    }

    const handleChangeAssociation = (event) => {
        setAssociation(event.target.value)

        // Also reset the discipline (otherwise there will be an exception in case of association change - as the selected value would be invalid)
        setDisciplines([])

        setParticipants([])
        setPlaylists([])
    }

    const handleChangeDisciplines = (event, value) => {
        setDisciplines(value)
    }

    const handleChangeParticipants = (event, value) => {
        setParticipants(value)
    }

    const handleChangePlaylists = (event, value) => {
        setPlaylists(value)
    }

    const handleChangeOpen = (event) => {
        setOpen(event.target.checked)
        setParticipants([])
    }

    return {
        name,
        handleChangeName,
        fromDate,
        handleChangeFromDate,
        toDate,
        handleChangeToDate,
        association,
        handleChangeAssociation,
        disciplines,
        handleChangeDisciplines,
        participants,
        handleChangeParticipants,
        playlists,
        handleChangePlaylists,
        open,
        handleChangeOpen,
        selectableUsers,
        selectablePlaylists,
        loading
    }
}
export default useGeneralEditCourseForm;